import React, { useState } from "react";
import {
  MyMessage,
  InputField,
  Select,
  AddPageShell,
  AddSection,
} from "../../components/Component";
import { codeOk } from "../../../lib/index";
import { useDoSeeDataContext } from "../../../context/DoSeeDataContext";
import { getOneGettingAround } from "../../../lib/sysNavigation/SysNavigation";
import { FOLDERS, image_kit_folder } from "../utils/Utils";
import { ImageKitUpload } from "../../../ui/ImageKitLibrary";
import { defaultImage } from "../../../lib/libraries/imageKit/ImageKit";
import { useGeoPositionContext } from "../utils/geoPosition";
import { _TYPES_ } from "../utils/types";
import Pricing from "../../components/Pricing";
import GenerateDescription from "../utils/GenerateDescription";
import { DescriptionSection } from "../utils/AddSections";
import { FiImage, FiSettings, FiDollarSign } from "react-icons/fi";
import { FaBus } from "react-icons/fa";

const Edit = () => {
  const { updateOneContent } = useDoSeeDataContext();
  const gtaround: any = getOneGettingAround();
  const {
    country_id,
    entry_label,
    country_name,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
  } = useGeoPositionContext();

  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState<any>("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const getting_around_id = gtaround?.getting_around_id;
  const [title, setTitle] = useState<any>(gtaround?.title);
  const [thumbnail, setThumbnail] = useState<any>(gtaround?.thumbnail);
  const [thumbnailId, setThumbnailId] = useState<any>(gtaround?.image_cdn_id);
  const [hashtags, setHashtags] = useState<any>(gtaround?.hashtag);
  const [gettingAroundStatus, setGettingAroundStatus] = useState<any>(
    gtaround?.status
  );
  const [description, setDescription] = useState<any>(gtaround?.description);
  const [expo, setExpo] = useState<any>(gtaround?.expo);
  const [pricing, setPricing] = useState<any>(gtaround?.Pricings);
  const [showDescriptionModal, setShowDescriptionModal] = useState<any>(null);

  const gtaroundRoutes = "/data/get/around";

  const responses = (isError: any, message: any) => {
    setError(isError);
    setLoading(false);
    setSuccess(!isError);
    setErrorMessage(message);
  };

  const updateOneGettingAroundHandler = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) {
      responses(true, "Image Required");
    } else {
      await sendDBRequest();
    }
  };

  const sendDBRequest = async () => {
    const data = {
      country_id: country_id ? country_id : gtaround?.country_id,
      first_level_id: first_level_id ? first_level_id : 0,
      second_level_id: second_level_id ? second_level_id : 0,
      third_level_id: third_level_id ? third_level_id : 0,
      fourth_level_id: fourth_level_id ? fourth_level_id : 0,
      title: title,
      slug: title ? title.toLowerCase().replace(/\s/g, "_") : null,
      description: description,
      thumbnail: thumbnail ? thumbnail : defaultImage,
      image_cdn_id: thumbnailId ? thumbnailId : "",
      hashtag: hashtags,
      status: gettingAroundStatus,
      expo: expo,
      pricing: pricing,
    };

    const { message, status } = await updateOneContent({
      data_id: getting_around_id,
      data: data,
      content: _TYPES_.GETTINGAROUND,
      position: {
        country_id,
        first_level_id,
        second_level_id,
        third_level_id,
        fourth_level_id,
      },
    });

    if (status === codeOk) responses(false, message);
    else responses(true, message);
  };

  const clearMessages = () => {
    setError(null as any);
    setSuccess(null as any);
  };

  return (
    <AddPageShell
      backRoute={gtaroundRoutes}
      eyebrow="DoSee Data • Getting Around"
      title="Edit Getting Around"
      subtitle={`Update transportation info for ${entry_label || "this destination"}.`}
      icon={<FaBus className="h-5 w-5" />}
      modals={
        showDescriptionModal ? (
          <GenerateDescription
            setDescription={setDescription}
            setShowDescriptionModal={setShowDescriptionModal}
          />
        ) : null
      }
      loading={loading}
      submitLabel="Save Changes"
      onSubmit={updateOneGettingAroundHandler}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage(null);
      }}
      message={
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />
      }
    >
      <AddSection
        title="Cover Image"
        hint="Photo of the transport mode."
        icon={<FiImage className="h-4 w-4" />}
      >
        <ImageKitUpload
          thumbnail={thumbnail}
          thumbnailId={thumbnailId}
          setThumbnail={setThumbnail}
          setThumbnailId={setThumbnailId}
          folder={image_kit_folder(country_name, FOLDERS.GETTING_AROUND)}
        />
      </AddSection>

      <AddSection title="Transport Details" hint="Title only — keep it short.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <InputField
            props={{
              id: "title",
              title: "Title",
              type: "text",
              value: title || "",
              onChange: (e: any) => {
                setTitle(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Pricing"
        hint="Optional pricing tiers."
        icon={<FiDollarSign className="h-4 w-4" />}
      >
        <Pricing pricing={pricing} setPricing={setPricing} />
      </AddSection>

      <AddSection
        title="Visibility"
        hint="Where this entry shows."
        icon={<FiSettings className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Select
            props={{
              id: "getting_around_status",
              title: "Status",
              option: ["visible", "invisible"],
              preselect:
                gettingAroundStatus !== undefined &&
                gettingAroundStatus !== null
                  ? [
                      {
                        name:
                          gettingAroundStatus === 1 ? "visible" : "invisible",
                      },
                    ]
                  : null,
              onChange: (e: any) => {
                if (e.target.selectedIndex === 0) setGettingAroundStatus(1);
                else setGettingAroundStatus(0);
                clearMessages();
              },
            }}
          />
          <Select
            props={{
              id: "gallery",
              title: "Show in Gallery",
              option: ["no", "yes"],
              preselect:
                expo !== undefined && expo !== null
                  ? [{ name: expo === 0 ? "no" : "yes" }]
                  : null,
              onChange: (e: any) => {
                const choice = e.target.value;
                setExpo(choice === "no" ? 0 : 1);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <DescriptionSection
        description={description}
        setDescription={setDescription}
        hashtags={hashtags}
        setHashtags={setHashtags}
        onGenerate={() => setShowDescriptionModal(true)}
        clearMessages={clearMessages}
      />
    </AddPageShell>
  );
};

export default Edit;
